/**
 * Shipping rules.
 *   - Zones: CABA and the three rings (cordones) of Greater Buenos Aires have a fixed price in pesos
 *     (setting shipping_rates_ars) and are dispatched within DISPATCH_HOURS. Anywhere else ("other")
 *     pays the flat "rest of the country" rate when the admin sets one, or is arranged on WhatsApp.
 *   - The zone is suggested from the city's partido; the shopper can change it.
 *   - Free shipping from a peso threshold, only while free_shipping_enabled is "on", and only to the
 *     zones in free_shipping_zones (CABA only since October 2026).
 */
import { usdCentsToArs } from "./money";

export const FREE_SHIPPING_MIN_ARS = 150_000;
export const DISPATCH_HOURS = 48;

export const SHIPPING_ZONES = ["caba", "cordon1", "cordon2", "cordon3", "other"] as const;
export type ShippingZone = (typeof SHIPPING_ZONES)[number];
export type RatedZone = Exclude<ShippingZone, "other">;
export const RATED_ZONES: RatedZone[] = ["caba", "cordon1", "cordon2", "cordon3"];
export const isShippingZone = (z: unknown): z is ShippingZone => typeof z === "string" && (SHIPPING_ZONES as readonly string[]).includes(z);
/** Zones with a fixed price, dispatched within DISPATCH_HOURS. */
export const isFastZone = (z: ShippingZone): z is RatedZone => z !== "other";

export const ZONE_LABEL: Record<ShippingZone, { es: string; en: string }> = {
  caba: { es: "CABA", en: "Buenos Aires City (CABA)" },
  cordon1: { es: "GBA 1er cordón", en: "Greater Buenos Aires, 1st ring" },
  cordon2: { es: "GBA 2do cordón", en: "Greater Buenos Aires, 2nd ring" },
  cordon3: { es: "GBA 3er cordón", en: "Greater Buenos Aires, 3rd ring" },
  other: { es: "Otra zona del país", en: "Elsewhere in Argentina" },
};

/** Pesos per zone at launch (the setting overrides them). */
export const DEFAULT_SHIPPING_RATES: Record<RatedZone, number> = { caba: 5000, cordon1: 6500, cordon2: 8000, cordon3: 12000 };

/** Partidos of each ring (Georef department names, province "06"). */
export const CORDONES: Record<Exclude<RatedZone, "caba">, string[]> = {
  cordon1: ["Avellaneda", "Lanús", "Lomas de Zamora", "La Matanza", "Morón", "San Isidro", "Vicente López", "General San Martín", "Tres de Febrero"],
  cordon2: [
    "Quilmes", "Berazategui", "Florencio Varela", "Almirante Brown", "Esteban Echeverría", "Ezeiza", "Merlo", "Moreno",
    "José C. Paz", "Malvinas Argentinas", "San Miguel", "San Fernando", "Tigre", "Hurlingham", "Ituzaingó",
  ],
  cordon3: [
    "Escobar", "Pilar", "General Rodríguez", "Marcos Paz", "Presidente Perón", "San Vicente", "Cañuelas", "La Plata", "Berisso",
    "Ensenada", "Zárate", "Campana", "Exaltación de la Cruz", "Luján", "General Las Heras", "Brandsen",
  ],
};

/** Zone suggested by the province (Georef id) and the city's partido; null while unknown. */
export function suggestZone(provinceId: string | null | undefined, department: string | null | undefined): ShippingZone | null {
  if (!provinceId) return null;
  if (provinceId === "02") return "caba";
  if (provinceId !== "06") return "other";
  if (!department) return null;
  for (const [zone, partidos] of Object.entries(CORDONES)) if (partidos.includes(department)) return zone as ShippingZone;
  return "other";
}

/** Pesos per zone; `other` (rest of the country) is null while shipping there is arranged on WhatsApp. */
export type ShippingRates = Record<RatedZone, number> & { other: number | null };

/** Rates from the setting (JSON), falling back to the defaults for anything missing or invalid. */
export function shippingRates(settingValue: string | null | undefined): ShippingRates {
  let parsed: Record<string, unknown> = {};
  try {
    parsed = settingValue ? (JSON.parse(settingValue) as Record<string, unknown>) : {};
  } catch {
    parsed = {};
  }
  const out: ShippingRates = { ...DEFAULT_SHIPPING_RATES, other: null };
  for (const z of RATED_ZONES) {
    const n = Number(parsed[z]);
    if (Number.isInteger(n) && n >= 0) out[z] = n;
  }
  const other = parsed.other === null || parsed.other === undefined ? NaN : Number(parsed.other);
  if (Number.isInteger(other) && other > 0) out.other = other;
  return out;
}

/** Cheapest home delivery (SEO "shipping from"). */
export function cheapestRate(rates: ShippingRates): number {
  return Math.min(...RATED_ZONES.map((z) => rates[z]), ...(rates.other ? [rates.other] : []));
}

/** Pickup at a point whose address is sent on WhatsApp after the purchase (setting pickup_enabled, on by default). */
export function pickupEnabled(settingValue: string | null | undefined): boolean {
  return settingValue !== "off";
}

/** Public hint of where the pickup point is (setting pickup_area); the exact address goes on WhatsApp. */
export const DEFAULT_PICKUP_AREA = "CABA";
export function pickupArea(settingValue: string | null | undefined): string {
  return settingValue?.trim().slice(0, 80) || DEFAULT_PICKUP_AREA;
}

export function freeShippingEnabled(settingValue: string | null | undefined): boolean {
  return settingValue === "on";
}

/** Zones that get free shipping (setting free_shipping_zones, a JSON list); every rated zone when unset. */
export function freeShippingZones(settingValue: string | null | undefined): RatedZone[] {
  try {
    const parsed = JSON.parse(settingValue ?? "null") as unknown;
    if (Array.isArray(parsed)) return RATED_ZONES.filter((z) => parsed.includes(z));
  } catch {
    // fall through
  }
  return [...RATED_ZONES];
}

/** "CABA" / "CABA y GBA 1er cordón" for texts. */
export function zonesText(zones: RatedZone[], locale: "es" | "en"): string {
  const names = zones.map((z) => ZONE_LABEL[z][locale]);
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} ${locale === "es" ? "y" : "and"} ${names[names.length - 1]}` : names[0] ?? "";
}

export function freeShippingMin(settingValue: string | null | undefined): number {
  const n = Number(settingValue);
  return Number.isFinite(n) && n > 0 ? n : FREE_SHIPPING_MIN_ARS;
}

/** Whether a cart (USD cents) reaches free shipping at the peso rate; null when the rate is unknown. */
export function qualifiesForFreeShipping(totalUsdCents: number, usdArsRate: number | null, minArs = FREE_SHIPPING_MIN_ARS): boolean | null {
  if (!usdArsRate) return null;
  return usdCentsToArs(totalUsdCents, usdArsRate) >= minArs;
}

export interface ShippingQuote {
  /** Pesos to charge (0 = free or pickup); null = arranged on WhatsApp after the purchase. */
  ars: number | null;
  free: boolean;
}

/** What shipping costs for this order. */
export function quoteShipping(
  delivery: "pickup" | "shipping",
  zone: ShippingZone | null,
  productsUsdCents: number,
  usdArsRate: number | null,
  settings: { rates: ShippingRates; freeEnabled: boolean; freeMinArs: number; freeZones: RatedZone[] },
): ShippingQuote {
  if (delivery === "pickup") return { ars: 0, free: false };
  if (!zone) return { ars: null, free: false };
  if (!isFastZone(zone)) return { ars: settings.rates.other, free: false };
  if (settings.freeEnabled && settings.freeZones.includes(zone) && qualifiesForFreeShipping(productsUsdCents, usdArsRate, settings.freeMinArs)) return { ars: 0, free: true };
  return { ars: settings.rates[zone], free: false };
}

/** Shipping for USD charges (Stripe, PayPal): the peso price at the day's rate, in cents, rounded up. */
export function shippingUsdCents(ars: number, usdArsRate: number): number {
  return ars > 0 ? Math.ceil((ars * 100) / usdArsRate - 1e-9) : 0;
}

/**
 * Rough area from an Argentine postal code, for the estimate on the product page. Accepts the old
 * 4 digits ("1425") and the CPA ("C1425DKP", "B1824ABC"): its letter is the province (C = CABA,
 * B = Buenos Aires). A code alone can't tell the GBA rings apart, so GBA is a range.
 */
export type PostalArea = "caba" | "gba" | "other";

const PROVINCE_LETTERS = "ABCDEFGHJKLMNPQRSTUVWXYZ";

export function postalArea(raw: string): PostalArea | null {
  const code = raw.trim().toUpperCase().replace(/\s+/g, "");
  const cpa = /^([A-Z])(\d{4})[A-Z]{0,3}$/.exec(code);
  const plain = /^\d{4}$/.exec(code);
  if (!cpa && !plain) return null;
  const letter = cpa?.[1] ?? null;
  if (letter && !PROVINCE_LETTERS.includes(letter)) return null;
  const n = Number(cpa ? cpa[2] : code);
  if (n < 1000) return null;
  if (letter === "C") return "caba";
  if (letter && letter !== "B") return "other";
  if (!letter && n <= 1499) return "caba";
  // Greater Buenos Aires and La Plata (3rd ring) use 1600–1999.
  if (n >= 1600 && n <= 1999) return "gba";
  return "other";
}

export type ShippingEstimate =
  | { kind: "exact"; ars: number; freeFromArs: number | null }
  | { kind: "range"; min: number; max: number; freeFromArs: number | null }
  | { kind: "arrange" };

/** What shipping would cost to that area (before the cart is known, so free shipping is a note). */
export function estimateShipping(area: PostalArea, settings: { rates: ShippingRates; freeEnabled: boolean; freeMinArs: number; freeZones: RatedZone[] }): ShippingEstimate {
  const freeFor = (zones: RatedZone[]) => (settings.freeEnabled && zones.some((z) => settings.freeZones.includes(z)) ? settings.freeMinArs : null);
  if (area === "caba") return { kind: "exact", ars: settings.rates.caba, freeFromArs: freeFor(["caba"]) };
  if (area === "gba") {
    const rings: RatedZone[] = ["cordon1", "cordon2", "cordon3"];
    const prices = rings.map((z) => settings.rates[z]);
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    return min === max ? { kind: "exact", ars: min, freeFromArs: freeFor(rings) } : { kind: "range", min, max, freeFromArs: freeFor(rings) };
  }
  return settings.rates.other ? { kind: "exact", ars: settings.rates.other, freeFromArs: null } : { kind: "arrange" };
}
